import type { TrackResult, IdentifyResult, UserTraits, GroupTraits } from '../interfaces/IAnalyticsProvider';
import { redactUrlShapedValues } from '../redaction';
import { BaseAnalyticsProvider } from './BaseAnalyticsProvider';

/**
 * posthog-js's `before_send` shape, restated locally.
 *
 * `posthog-js` is an OPTIONAL peer dependency of this package (the server build
 * uses `posthog-node` and must not need it), so a `import type { BeforeSendFn }`
 * would make the type-check of a consumer without it fail. The contract is two
 * lines and stable: one capture in, the same capture or `null` out.
 *
 * `$set_once` is listed because it is the half that gets missed: it is a
 * SIBLING of `properties`, not a key inside it, and `get_initial_props()` puts
 * the session's landing URL there.
 */
type CaptureLike = {
    properties?: Record<string, unknown>;
    $set_once?: Record<string, unknown>;
} | null;

/**
 * The single `before_send` installed at `init` — the LAST step before the wire,
 * which is the only place that covers properties the SDK adds itself (the call
 * site for `$current_url` IS posthog-js).
 *
 * Ruling R19: it walks `properties` AND `$set_once`, redacting by KEY SHAPE
 * rather than a list of three names. The list version missed six more fields
 * carrying the same URL — `$session_entry_*`, merged into every event, and
 * `$initial_*` on `$set_once` — each populated exactly when the session starts
 * on the table page. See `../redaction`'s `isUrlShapedKey`.
 *
 * Total and non-throwing — an exception here would drop the event.
 */
export function redactPostHogCapture(capture: CaptureLike): CaptureLike {
    if (!capture) return capture;
    redactUrlShapedValues(capture.properties);
    redactUrlShapedValues(capture.$set_once);
    return capture;
}

/**
 * PostHog Analytics Provider
 *
 * Supports both client-side (posthog-js) and server-side (posthog-node).
 * Detects environment automatically.
 *
 * Required env vars:
 * - POSTHOG_API_KEY (or NEXT_PUBLIC_POSTHOG_API_KEY for client)
 * - POSTHOG_HOST (optional, defaults to https://us.i.posthog.com)
 *
 * Install: pnpm add posthog-js (client) or posthog-node (server)
 */
export class PostHogAnalyticsProvider extends BaseAnalyticsProvider {
    readonly providerName = 'PostHog';
    private client: any = null;
    private apiKey: string;
    private host: string;

    constructor(app: string, options?: { apiKey?: string; host?: string }) {
        super(app);
        // Prefer explicit options. REQUIRED for Vite browser bundles: Vite
        // only inlines `import.meta.env.VITE_*`, never `process.env.VITE_*`,
        // so a Vite caller must read its key from import.meta.env and pass it
        // in here (mirrors how SentryErrorTracker takes an explicit dsn).
        // Next.js inlines `process.env.NEXT_PUBLIC_*`, so the env fallback
        // works there, but callers should still pass it for symmetry.
        this.apiKey = options?.apiKey
            || process.env.POSTHOG_API_KEY
            || process.env.NEXT_PUBLIC_POSTHOG_API_KEY
            || process.env.VITE_POSTHOG_API_KEY
            || '';
        this.host = options?.host
            || process.env.POSTHOG_HOST
            || process.env.NEXT_PUBLIC_POSTHOG_HOST
            || 'https://us.i.posthog.com';
    }

    protected isProviderConfigured(): boolean {
        return !!this.apiKey && this.client !== null;
    }

    /**
     * Initialize the PostHog client. Must be called before tracking.
     * Auto-detects client vs server environment.
     */
    async initialize(): Promise<void> {
        if (!this.apiKey) return;

        if (typeof window !== 'undefined') {
            // Client-side
            const posthogJs = await import('posthog-js');
            this.client = posthogJs.default;
            this.client.init(this.apiKey, {
                api_host: this.host,
                capture_pageview: false,
                capture_pageleave: true,
                autocapture: false,
                session_recording: { maskAllInputs: true, maskTextSelector: '[data-mask]' },
                persistence: 'localStorage+cookie',
                // Ruling R18 — strip capability tokens out of the URLs the SDK
                // attaches to every capture. This cannot be done at the call
                // sites: the call site for `$current_url` IS posthog-js.
                before_send: redactPostHogCapture,
            });
            this.client.register({
                app: this.config.app,
                environment: this.config.environment,
            });
        } else {
            // Server-side
            const { PostHog } = await import('posthog-node');
            this.client = new PostHog(this.apiKey, {
                host: this.host,
                flushInterval: 10_000,
            });
        }
    }

    protected trackRaw(event: string, properties: Record<string, unknown>): TrackResult {
        if (typeof window !== 'undefined') {
            this.client.capture(event, properties);
        } else {
            const { userId, ...rest } = properties;
            this.client.capture({
                distinctId: (userId as string) || 'server',
                event,
                properties: rest,
            });
        }
        return { success: true };
    }

    protected identifyRaw(userId: string, traits?: UserTraits): IdentifyResult {
        if (typeof window !== 'undefined') {
            this.client.identify(userId, traits);
        } else {
            this.client.identify({ distinctId: userId, properties: traits });
        }
        return { success: true };
    }

    protected pageRaw(name: string, properties: Record<string, unknown>): TrackResult {
        if (typeof window !== 'undefined') {
            this.client.capture('$pageview', { $current_url: name, ...properties });
        } else {
            const { userId, ...rest } = properties;
            this.client.capture({
                distinctId: (userId as string) || 'server',
                event: '$pageview',
                properties: { $current_url: name, ...rest },
            });
        }
        return { success: true };
    }

    protected groupRaw(groupId: string, groupType: string, traits?: GroupTraits): void {
        if (typeof window !== 'undefined') {
            this.client.group(groupType, groupId, traits);
        } else {
            this.client.groupIdentify({ groupType, groupKey: groupId, properties: traits });
        }
    }

    protected resetRaw(): void {
        if (typeof window !== 'undefined') {
            this.client?.reset();
        }
    }

    protected async flushRaw(): Promise<void> {
        if (typeof window === 'undefined' && this.client) {
            await this.client.flush();
        }
    }

    override setOptOut(optOut: boolean): void {
        if (typeof window !== 'undefined' && this.client) {
            if (optOut) {
                this.client.opt_out_capturing();
            } else {
                this.client.opt_in_capturing();
            }
        }
    }

    /** Shutdown server-side client */
    async shutdown(): Promise<void> {
        if (typeof window === 'undefined' && this.client) {
            await this.client.shutdown();
        }
    }
}
